import { Box, List, ListItem, ListItemIcon, ListItemText } from "@mui/material";
import {
  IconBuildingBank,
  IconCircleCheckFilled,
  IconCoins,
  IconHourglassHigh,
  IconReceiptRefund,
} from "@tabler/icons-react";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Button from "../../components/ui/Button";
import { LoadingSpinner } from "../../components/ui/LoadingSpinner";
import Text from "../../components/ui/Text";
import { useToast } from "../../contexts/ToastContext";
import apiService from "../../services/api";

const ConnectingStripeAccount = () => {
  const navigation = useNavigate();
  const { showToast } = useToast();
  const [searchParams] = useSearchParams();
  const code = searchParams.get("code");

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!code) return;
    setLoading(true);
    const connectStripeAccount = async () => {
      try {
        const response = await apiService.connectStripeAccount({ code });
        if (response.success) {
          showToast(response.message, "success");

          // Broadcast message to other tabs
          const channel = new BroadcastChannel("stripe_connection");
          channel.postMessage({
            type: "STRIPE_CONNECTED",
            success: true,
            message: response.message,
            data: response.data,
          });
          channel.close();
          setLoading(false);
        }
      } catch (error) {
        showToast(error.error, "error");
        navigation("/payments/setup");
      }
    };
    connectStripeAccount();
  }, [code]);

  const features = [
    {
      icon: <IconCoins size={24} />,
      title: "Players pay to join",
      description:
        "You can create games and we'll handle the rest so you can get on with the game.",
    },
    {
      icon: <IconBuildingBank size={24} />,
      title: "Quick payouts",
      description: "Money from your bank 24 hours after the game is finished.",
    },
    {
      icon: <IconReceiptRefund size={24} />,
      title: "Hassle free refunds",
      description:
        "Player cancellations 24 hours before will be automatically refunded in credits.",
    },
    {
      icon: <IconHourglassHigh size={24} />,
      title: "Reduce no-shows",
      description:
        "Player deposits are held for 24 hours before the game to reduce no-shows.",
    },
  ];

  if (loading) {
    return (
      <Box>
        <LoadingSpinner />
      </Box>
    );
  }

  return (
    <Box sx={{ py: { xs: 0, md: 3 } }}>
      <Box
        maxWidth={"sm"}
        sx={{
          flex: { xs: "none", md: 1 },
          backgroundColor: { xs: "transparent", md: "white" },
          borderRadius: { xs: 0, md: "8px" },
          boxShadow: { xs: "none", md: "0 2px 8px rgba(0,0,0,0.1)" },
          overflow: "hidden",
          p: { xs: 2, md: 3 },
          mx: "auto",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            mx: "auto",
          }}
        >
          <IconCircleCheckFilled size={72} color="#00C950" />
        </Box>
        <Text
          variant="pageHeader"
          sx={{
            my: 2,
            textAlign: "center",
          }}
        >
          You’re all set to get paid!
        </Text>
        <Text
          variant="paragraph"
          component="p"
          sx={{ fontSize: "14px", textAlign: "center" }}
        >
          Your Stripe account is connected successfully. You can close this
          window and finish creating your game. Players will now be able to pay
          in-app.
        </Text>
        {/* Features List */}
        <List sx={{ p: 0, mb: "32px" }}>
          {features.map((feature, index) => (
            <ListItem
              key={index}
              sx={{
                px: 0,
                py: 0.5,
                display: "flex",
                gap: "8px",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <ListItemIcon
                sx={{
                  color: "black",
                  minWidth: "0px",
                  width: "48px",
                  height: "48px",
                  borderRadius: "50%",
                  background: " #F5F5F5",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {feature.icon}
              </ListItemIcon>
              <ListItemText
                primary={feature.title}
                secondary={feature.description}
                sx={{
                  "& .MuiTypography-root": {
                    fontSize: "16px",
                    fontWeight: 700,
                    color: "#000",
                  },
                  "& .MuiListItemText-secondary": {
                    fontWeight: 400,
                    fontSize: "14px",
                    color: "#666",
                    lineHeight: "1.4",
                  },
                }}
              />
            </ListItem>
          ))}
        </List>

        <Button
          type="button"
          onClick={() => {
            window.close();
          }}
          variant="primary"
          sx={{
            width: { xs: "100%", md: "auto" },
            mx: "auto",
            display: "block",
          }}
        >
          Return to Findaside
        </Button>
      </Box>
    </Box>
  );
};

export default ConnectingStripeAccount;
